import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Filter as FilterIcon, X, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'

export interface FilterOption {
  key: string
  label: string
  type: 'text' | 'select' | 'date' | 'number' | 'boolean'
  options?: Array<{ value: string; label: string }>
  placeholder?: string
  min?: number
  max?: number
}

export interface FilterValue {
  key: string
  value: any
  label: string
}

export interface FilterBarProps {
  filters: FilterOption[]
  onFilterChange: (filters: FilterValue[]) => void
  onSearch?: (query: string) => void
  searchPlaceholder?: string
  className?: string
  showAdvancedFilters?: boolean
  onAdvancedFiltersToggle?: (show: boolean) => void
  initialValues?: Record<string, any>
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  onSearch,
  searchPlaceholder = 'Rechercher...',
  className = '',
  showAdvancedFilters: externalShowAdvancedFilters,
  onAdvancedFiltersToggle,
  initialValues = {},
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterValues, setFilterValues] = useState<Record<string, any>>(initialValues)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(externalShowAdvancedFilters || false)
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([])
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Mettre à jour les valeurs initiales
  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      setFilterValues(initialValues)
      updateActiveFilters(initialValues)
    }
  }, [initialValues])

  // Mettre à jour les filtres actifs
  const updateActiveFilters = (values: Record<string, any>) => {
    const newActiveFilters: FilterValue[] = []
    
    Object.entries(values).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const filter = filters.find(f => f.key === key)
        if (filter) {
          let displayValue = value
          
          if (filter.type === 'select' && filter.options) {
            const option = filter.options.find(opt => opt.value === value)
            displayValue = option ? option.label : value
          } else if (filter.type === 'boolean') {
            displayValue = value ? 'Oui' : 'Non'
          }
          
          newActiveFilters.push({
            key,
            value,
            label: `${filter.label}: ${displayValue}`
          })
        }
      }
    })
    
    setActiveFilters(newActiveFilters)
    onFilterChange(newActiveFilters)
  }

  // Gestion de la recherche avec debounce
  const handleSearch = useCallback((value: string) => {
    console.log('[DEBUG] FilterBar handleSearch called with value:', value)
    setSearchQuery(value)
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Set new timeout for debounce (300ms)
    searchTimeoutRef.current = setTimeout(() => {
      console.log('[DEBUG] FilterBar calling onSearch with value:', value)
      if (onSearch) {
        onSearch(value)
      }
    }, 300)
  }, [onSearch])

  // Gestion des changements de filtre
  const handleFilterChange = (key: string, value: any) => {
    const newValues = { ...filterValues, [key]: value }
    setFilterValues(newValues)
    updateActiveFilters(newValues)
  }

  // Réinitialiser tous les filtres
  const handleResetFilters = () => {
    const resetValues: Record<string, any> = {}
    filters.forEach(filter => {
      resetValues[filter.key] = ''
    })
    
    setFilterValues(resetValues)
    setSearchQuery('')
    setActiveFilters([])
    
    // Clear search timeout and trigger empty search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    if (onSearch) {
      onSearch('')
    }
    
    if (onSearch) {
      onSearch('')
    }
    
    onFilterChange([])
  }

  // Supprimer un filtre spécifique
  const handleRemoveFilter = (key: string) => {
    const newValues = { ...filterValues, [key]: '' }
    setFilterValues(newValues)
    updateActiveFilters(newValues)
  }

  // Basculer les filtres avancés
  const toggleAdvancedFilters = () => {
    const newState = !showAdvancedFilters
    setShowAdvancedFilters(newState)
    onAdvancedFiltersToggle?.(newState)
  }

  // Rendu d'un champ de filtre
  const renderFilterField = (filter: FilterOption) => {
    const value = filterValues[filter.key] || ''
    
    switch (filter.type) {
      case 'text':
        return (
          <Input
            type="text"
            placeholder={filter.placeholder || `Filtrer par ${filter.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className="w-full"
          />
        )
        
      case 'number':
        return (
          <div className="flex space-x-2">
            <Input
              type="number"
              placeholder={filter.placeholder || 'Min'}
              value={value?.min || ''}
              onChange={(e) => handleFilterChange(filter.key, {
                ...(typeof value === 'object' ? value : {}),
                min: e.target.value ? Number(e.target.value) : ''
              })}
              min={filter.min}
              className="w-full"
            />
            <Input
              type="number"
              placeholder="Max"
              value={value?.max || ''}
              onChange={(e) => handleFilterChange(filter.key, {
                ...(typeof value === 'object' ? value : {}),
                max: e.target.value ? Number(e.target.value) : ''
              })}
              max={filter.max}
              className="w-full"
            />
          </div>
        )
        
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Tous</option>
            {filter.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
        
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className="w-full"
          />
        )
        
      case 'boolean':
        return (
          <select
            value={value === '' ? '' : value ? 'true' : 'false'}
            onChange={(e) => handleFilterChange(filter.key, e.target.value === '' ? '' : e.target.value === 'true')}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Tous</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        )
        
      default:
        return null
    }
  }

  // Filtres de base (premiers 2-3 filtres)
  const basicFilters = filters.slice(0, 3)
  const advancedFilters = filters.slice(3)

  return (
    <div className={`w-full space-y-4 ${className}`}>
      {/* Barre de recherche et filtres de base */}
      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
        {/* Recherche */}
        {onSearch && (
          <div className="min-w-0 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}
        
        {/* Filtres de base */}
        {basicFilters.map((filter) => (
          <div key={filter.key} className="w-full md:w-48 md:flex-none">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {filter.label}
            </label>
            {renderFilterField(filter)}
          </div>
        ))}
        
        {/* Boutons d'action */}
        <div className="flex shrink-0 items-end space-x-2">
          {advancedFilters.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={toggleAdvancedFilters}
              className="flex items-center"
            >
              <FilterIcon className="mr-2 h-4 w-4" />
              Plus de filtres
              {showAdvancedFilters ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          )}
          
          {(activeFilters.length > 0 || searchQuery) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleResetFilters}
              className="flex items-center"
            >
              <X className="mr-2 h-4 w-4" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>
      
      {/* Filtres actifs */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">Filtres actifs:</span>
          {activeFilters.map((filter) => (
            <div
              key={filter.key}
              className="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-800"
            >
              {filter.label}
              <button
                type="button"
                onClick={() => handleRemoveFilter(filter.key)}
                className="ml-2 text-primary-600 hover:text-primary-800"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Filtres avancés */}
      {showAdvancedFilters && advancedFilters.length > 0 && (
        <div className="rounded-lg border bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">Filtres avancés</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleAdvancedFilters}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {advancedFilters.map((filter) => (
              <div key={filter.key}>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {filter.label}
                </label>
                {renderFilterField(filter)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Helper pour créer des options de filtre
export const createFilterOptions = <T extends Record<string, any>>(
  data: T[],
  key: keyof T,
  labelKey?: keyof T
): Array<{ value: string; label: string }> => {
  const uniqueValues = Array.from(new Set(data.map(item => item[key])))
  
  return uniqueValues.map(value => ({
    value: String(value),
    label: labelKey 
      ? String(data.find(item => item[key] === value)?.[labelKey] || value)
      : String(value)
  }))
}

// Filtres prédéfinis communs
export const statusFilter: FilterOption = {
  key: 'status',
  label: 'Statut',
  type: 'select',
  options: [
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
    { value: 'pending', label: 'En attente' },
  ]
}

export const dateRangeFilter: FilterOption = {
  key: 'date_range',
  label: 'Période',
  type: 'date',
  placeholder: 'Sélectionner une date'
}

export const booleanFilter = (key: string, label: string): FilterOption => ({
  key,
  label,
  type: 'boolean'
})

export default FilterBar